import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  addDoc,
  limit,
  writeBatch,
  getDoc
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { LoadingSpinner } from "./Spinner";
import "./NotificationSystem.css";

// 🆕 MEJORA: Throttling para evitar spam de notificaciones
const NOTIFICATION_THROTTLE_MS = 5000; // 5 segundos entre notificaciones del mismo tipo
const throttleMap = new Map();

// Función para crear notificaciones con throttling
export const createNotification = async (userId, type, data) => {
  try {
    // 🆕 MEJORA: Prevenir spam de notificaciones
    const throttleKey = `${userId}-${type}-${data.routineId || data.followerId || 'default'}`;
    const now = Date.now();
    const lastNotification = throttleMap.get(throttleKey);
    
    if (lastNotification && (now - lastNotification) < NOTIFICATION_THROTTLE_MS) {
   
      return;
    }
    

    
    await addDoc(collection(db, "notifications"), {
      userId,
      type, // 'follow', 'routine_copied', 'new_follower', 'new_routine', 'routine_updated'
      message: generateNotificationMessage(type, data),
      data,
      read: false,
      createdAt: serverTimestamp()
    });
    
    // 🆕 MEJORA: Registrar en throttle map
    throttleMap.set(throttleKey, now);
    
  
  } catch (error) {
    console.error("Error creando notificación:", error);
  }
};

// Generar mensaje de notificación - MEJORADO
const generateNotificationMessage = (type, data) => {
  switch (type) {
    case 'follow':
      return `${data.followerNickname || data.followerName} comenzó a seguirte`;
    case 'routine_copied':
      return `${data.copierNickname || data.userName} copió tu rutina "${data.routineName}"`;
    case 'new_follower':
      return `¡Tienes un nuevo seguidor: ${data.followerNickname || data.followerName}!`;
    case 'new_routine':
      return `${data.authorNickname || data.authorName} ha creado una nueva rutina: "${data.routineName}"`;
    case 'routine_updated':
      return `${data.authorNickname || data.authorName} ha actualizado su rutina: "${data.routineName}"`;
    // NUEVO: Mensajes para dietas
    case 'new_diet':
      return `${data.authorNickname || data.authorName} ha creado una nueva dieta: "${data.dietName}"`;
    case 'diet_updated':
      return `${data.authorNickname || data.authorName} ha actualizado su dieta: "${data.dietName}"`;
    default:
      return 'Nueva notificación';
  }
};

// 🆕 FUNCIONES HELPER PARA NAVEGACIÓN
// Determinar si una notificación es clickeable
const isNotificationClickable = (notification) => {
  const { type, data } = notification;
  if (!data) return false;
  switch (type) {
    case 'follow':
    case 'new_follower':
      return !!data.followerId;
    case 'routine_copied':
      return !!data.copierId;
    case 'new_routine':
    case 'routine_updated':
    case 'new_diet':
    case 'diet_updated':
      return !!data.authorId;
    default:
      return false;
  }
};

// Obtener tooltip descriptivo para la notificación
const getNotificationTooltip = (notification) => {
  const { type, data } = notification;
  if (!data) return '';
  switch (type) {
    case 'follow':
    case 'new_follower':
      return data.followerId ? `Ver perfil de ${data.followerName}` : '';
    case 'routine_copied':
      return data.copierId ? `Ver perfil de ${data.userName}` : '';
    case 'new_routine':
    case 'routine_updated':
      if (data.routineId) {
        return `Ver rutina "${data.routineName}"`;
      } else if (data.authorId) {
        return `Ver perfil de ${data.authorName}`;
      }
      return '';
    case 'new_diet':
    case 'diet_updated':
      return data.authorId ? `Ver perfil de ${data.authorName}` : '';
    default:
      return '';
  }
};

// Obtener ruta de navegación para la notificación
const getNotificationRoute = (notification) => {
  const { type, data } = notification;
  if (!data) return null;
  
  switch (type) {
    case 'follow':
    case 'new_follower':
      return data.followerId ? `/profile/${data.followerId}` : null;
    case 'routine_copied':
      return data.copierId ? `/profile/${data.copierId}` : null;
    case 'new_routine':
    case 'routine_updated':
      // Prioridad: rutina específica, luego perfil del autor
      if (data.routineId) {
        return `/routine/${data.routineId}`;
      } else if (data.authorId) {
        return `/profile/${data.authorId}`;
      }
      return null;
    default:
      return null;
  }
};

// 🆕 MEJORA: Hook para detectar si el usuario está activo
const useUserActivity = () => {
  const [isActive, setIsActive] = useState(true);
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsActive(!document.hidden);
    };
    
    const handleFocus = () => setIsActive(true);
    const handleBlur = () => setIsActive(false);
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);
  
  return isActive;
};

// Componente principal de notificaciones - MEJORADO
export default function NotificationSystem({ onNavigateToProfile, onNavigateToRoutine, onNotificationBellClick, onNotificationPanelClose }) {
  const [user] = useAuthState(auth);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [showDeleteAll, setShowDeleteAll] = useState(false);
  // 🆕 MEJORA: Estados adicionales
  const [lastSeenCount, setLastSeenCount] = useState(0);
  const [animateButton, setAnimateButton] = useState(false);
  
  const panelRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const isActive = useUserActivity();

  // Detectar cierre del panel de notificaciones
  const prevShowPanel = useRef(showPanel);
  useEffect(() => {
    if (prevShowPanel.current && !showPanel) {
      if (onNotificationPanelClose) onNotificationPanelClose();
    }
    prevShowPanel.current = showPanel;
  }, [showPanel, onNotificationPanelClose]);

  // 🆕 MEJORA: Función optimizada para marcar como leída (MOVIDA ARRIBA)
  const markAsRead = useCallback(async (notificationId) => {
    try {
  
      await updateDoc(doc(db, "notifications", notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Error marcando notificación como leída:", error);
    }
  }, []);

  // 🆕 MEJORA: Animar botón cuando llegan notificaciones nuevas
  useEffect(() => {
    if (unreadCount > lastSeenCount && isActive) {
      setAnimateButton(true);
      const timer = setTimeout(() => setAnimateButton(false), 1000);
      return () => clearTimeout(timer);
    }
    setLastSeenCount(unreadCount);
  }, [unreadCount, lastSeenCount, isActive]);

  // 🆕 MEJORA: Auto-marcar como leídas cuando se abre el panel
  useEffect(() => {
    if (showPanel && unreadCount > 0) {
      const timer = setTimeout(() => {
        // Auto-marcar como leídas después de 3 segundos de tener el panel abierto
        const unreadNotifications = notifications.filter(n => !n.read).slice(0, 5); // Solo las primeras 5
        unreadNotifications.forEach(notification => {
          markAsRead(notification.id);
        });
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [showPanel, unreadCount, notifications, markAsRead]);

  // Cerrar panel cuando se hace click fuera - MEJORADO
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowPanel(false);
        setShowDeleteAll(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowPanel(false);
        setShowDeleteAll(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showPanel]);

  // 🆕 MEJORA: Listener de notificaciones más robusto
  useEffect(() => {
    // Limpiar listener anterior
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!user) {
      setLoading(false);
      setNotifications([]);
      setUnreadCount(0);
      return;
    }



    try {
      const notificationsQuery = query(
        collection(db, "notifications"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(50)
      );

      unsubscribeRef.current = onSnapshot(
        notificationsQuery,
        (snapshot) => {
   
          
          const notificationsList = [];
          let unread = 0;

          snapshot.forEach((docRef) => {
            const data = { id: docRef.id, ...docRef.data() };
            notificationsList.push(data);
            if (!data.read) unread++;
          });

          setNotifications(notificationsList);
          setUnreadCount(unread);
          setLoading(false);
          setError(null);
          
     
        },
        (error) => {
          console.error("Error en listener de notificaciones:", error);
          setError("Error al cargar notificaciones");
          setLoading(false);
        }
      );
    } catch (error) {
      console.error("Error configurando listener:", error);
      setError("Error de configuración");
      setLoading(false);
    }

    // Cleanup
    return () => {
      if (unsubscribeRef.current) {

        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user]); // 🔧 CORREGIDO: user completo en lugar de user?.uid

  // 🆕 MEJORA: Batch optimizado para marcar todas como leídas
  const markAllAsRead = useCallback(async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      if (unreadNotifications.length === 0) return;
      

      
      // Usar batch para mejor rendimiento
      const batch = writeBatch(db);
      
      unreadNotifications.forEach(notification => {
        const notificationRef = doc(db, "notifications", notification.id);
        batch.update(notificationRef, { 
          read: true,
          readAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error("Error marcando todas las notificaciones como leídas:", error);
    }
  }, [notifications]);

  // Función para borrar notificación individual - MEJORADA
  const deleteNotification = useCallback(async (notificationId, event) => {
    event.stopPropagation();
    
    try {
     
      setDeletingIds(prev => new Set([...prev, notificationId]));
      
      await deleteDoc(doc(db, "notifications", notificationId));
  
    } catch (error) {
      console.error("Error borrando notificación:", error);
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  }, []);

  // Función para borrar todas las notificaciones - IGUAL
  const deleteAllNotifications = useCallback(async () => {
    try {
     
      setLoading(true);
      
      const batch = writeBatch(db);
      
      notifications.forEach(notification => {
        const notificationRef = doc(db, "notifications", notification.id);
        batch.delete(notificationRef);
      });
      
      await batch.commit();

      setShowDeleteAll(false);
    } catch (error) {
      console.error("Error borrando todas las notificaciones:", error);
      setLoading(false);
    }
  }, [notifications]);

  const formatNotificationTime = useCallback((timestamp) => {
    if (!timestamp || !timestamp.toDate) {
      return "Hace un momento";
    }
    
    try {
      const now = new Date();
      const notificationTime = timestamp.toDate();
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
      
      if (diffInMinutes < 1) return "Ahora";
      if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `Hace ${diffInHours}h`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `Hace ${diffInDays}d`;
      
      return notificationTime.toLocaleDateString();
    } catch (error) {
      console.error("Error formateando tiempo:", error);
      return "Hace un momento";
    }
  }, []);

  const getNotificationIcon = useCallback((type) => {
    switch (type) {
      case 'follow':
        return '👤';
      case 'routine_copied':
        return '📋';
      case 'new_follower':
        return '🎉';
      case 'new_routine':
        return '✨';
      case 'routine_updated':
        return '🔄';
      case 'new_diet':
      case 'diet_updated':
        return '🥗';
      default:
        return '��';
    }
  }, []);

  const getNotificationMessage = useCallback((notification) => {
    if (notification.message) {
      return notification.message;
    }
    return generateNotificationMessage(notification.type, notification.data || {});
  }, []);

  // Helper para obtener la URL de foto de perfil relevante según el tipo de notificación
  const getNotificationAvatar = (notification) => {
    const { type, data } = notification;
    if (!data) return "/default-avatar.png";
    switch (type) {
      case 'follow':
      case 'new_follower':
        return data.followerCustomPhotoURL || data.followerPhotoURL || data.customPhotoURL || data.photoURL || "/default-avatar.png";
      case 'routine_copied':
        return data.copierCustomPhotoURL || data.copierPhotoURL || data.customPhotoURL || data.photoURL || "/default-avatar.png";
      case 'new_routine':
      case 'routine_updated':
      case 'new_diet':
      case 'diet_updated':
        return data.authorCustomPhotoURL || data.authorPhotoURL || data.customPhotoURL || data.photoURL || "/default-avatar.png";
      default:
        return "/default-avatar.png";
    }
  };

  const handleNotificationClick = useCallback((notification) => {
    // Marcar como leída si no lo está
    if (!notification.read) {
      markAsRead(notification.id);
    }
    // Verificar si la notificación es clickeable
    if (!isNotificationClickable(notification)) {
      return; // No hacer nada si no es clickeable
    }
    // Obtener la ruta de navegación
    // MODIFICADO: Para new_routine, routine_updated, new_diet y diet_updated, siempre ir al perfil del autor
    try {
      const { type, data } = notification;
      if (
        type === 'new_routine' ||
        type === 'routine_updated' ||
        type === 'new_diet' ||
        type === 'diet_updated'
      ) {
        if (data.authorId && onNavigateToProfile) {
          onNavigateToProfile(data.authorId);
        }
      } else {
        // Para perfiles (follow, new_follower, routine_copied)
        const userId = data.followerId || data.copierId;
        if (userId && onNavigateToProfile) {
          onNavigateToProfile(userId);
        }
      }
      // Cerrar el panel después de navegar
      setShowPanel(false);
    } catch (error) {
      console.error('Error navegando a la notificación:', error);
    }
  }, [markAsRead, onNavigateToProfile, onNavigateToRoutine]);

  const togglePanel = useCallback(() => {

    setShowPanel(!showPanel);
    setShowDeleteAll(false);
  }, [showPanel]);

  if (!user) return null;

  return (
    <div className="notification-system" ref={panelRef}>
      {/* Botón de notificaciones - MEJORADO */}
      <button 
        className={`notification-button ${unreadCount > 0 ? 'has-unread' : ''} ${animateButton ? 'animate-pulse' : ''}`}
        onClick={(e) => {
          if (onNotificationBellClick) onNotificationBellClick();
          togglePanel();
        }}
        title={`${unreadCount} notificaciones no leídas`}
        aria-label={`Notificaciones: ${unreadCount} no leídas`}
      >
        <span className="notification-bell">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Panel de notificaciones */}
      {showPanel && (
        <div className="notification-panel">
          <div className="notification-header">
            <h3 className="notification-title">
              Notificaciones
              {unreadCount > 0 && (
                <span className="unread-count">({unreadCount})</span>
              )}
            </h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button 
                  onClick={markAllAsRead}
                  className="mark-all-read-btn"
                  title="Marcar todas como leídas"
                  aria-label="Marcar todas como leídas"
                >
                  ✓
                </button>
              )}
              {notifications.length > 0 && (
                <button 
                  onClick={() => setShowDeleteAll(!showDeleteAll)}
                  className="delete-all-btn"
                  title="Borrar todas las notificaciones"
                  aria-label="Borrar todas las notificaciones"
                >
                  🗑️
                </button>
              )}
              <button 
                onClick={() => setShowPanel(false)}
                className="close-panel-btn"
                title="Cerrar"
                aria-label="Cerrar panel"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Confirmación para borrar todas */}
          {showDeleteAll && (
            <div className="delete-all-confirmation">
              <div className="delete-confirmation-content">
                <p>¿Estás seguro de que quieres borrar todas las notificaciones?</p>
                <div className="delete-confirmation-actions">
                  <button 
                    onClick={() => setShowDeleteAll(false)}
                    className="cancel-delete-btn"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={deleteAllNotifications}
                    className="confirm-delete-btn"
                    disabled={loading}
                  >
                    {loading ? "Borrando..." : "Borrar todas"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="notification-list">
            {loading ? (
              <LoadingSpinner text="Cargando notificaciones..." />
            ) : error ? (
              <div className="notification-error">
                <div className="error-icon">⚠️</div>
                <h4>Error al cargar</h4>
                <p>{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="retry-btn"
                >
                  Reintentar
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <div className="empty-icon">🔔</div>
                <h4>No hay notificaciones</h4>
                <p>Te notificaremos cuando tengas actividad nueva</p>
              </div>
            ) : (
              <div className="notifications-container">
                {notifications.map((notification) => {
                  const isDiet = notification.type === 'new_diet' || notification.type === 'diet_updated';
                  const isRoutine = notification.type === 'new_routine' || notification.type === 'routine_updated';
                  const isClickable = isNotificationClickable(notification);
                  const tooltip = getNotificationTooltip(notification);
                  
                  return (
                    <div 
                      key={notification.id}
                      className={`notification-item ${!notification.read ? 'unread' : 'read'} ${
                        deletingIds.has(notification.id) ? 'deleting' : ''
                      } ${isClickable ? 'clickable' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                      data-type={notification.type}
                      role="button"
                      tabIndex={0}
                      title={tooltip}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleNotificationClick(notification);
                        }
                      }}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="notification-content">
                        <p className="notification-message">
                          {/* Mostrar avatar para dietas y rutinas */}
                          {(isDiet || isRoutine || notification.type === 'routine_copied' || notification.type === 'follow' || notification.type === 'new_follower') && (
                            <NotificationAvatar
                              src={getNotificationAvatar(notification)}
                              alt="Avatar"
                            />
                          )}
                          {getNotificationMessage(notification)}
                          {isClickable && (
                            <span className="notification-action-hint">
                              →
                            </span>
                          )}
                        </p>
                        <span className="notification-time">
                          {formatNotificationTime(notification.createdAt)}
                        </span>
                        
                        {/* Mostrar detalles adicionales para notificaciones de rutinas */}
                        {(notification.type === 'new_routine' || notification.type === 'routine_updated') && notification.data && (
                          <div className="routine-notification-details">
                            {notification.data.routineCategory && (
                              <span className="routine-category-tag">
                                {notification.data.routineCategory}
                              </span>
                            )}
                            {notification.data.routineDifficulty && (
                              <span className="routine-difficulty-tag">
                                {notification.data.routineDifficulty}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="notification-actions">
                        {!notification.read && (
                          <div className="notification-unread-dot"></div>
                        )}
                        <button
                          onClick={(e) => deleteNotification(notification.id, e)}
                          className="delete-notification-btn"
                          title="Borrar notificación"
                          disabled={deletingIds.has(notification.id)}
                          aria-label="Borrar notificación"
                        >
                          {deletingIds.has(notification.id) ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <span className="notification-count">
                {notifications.length} notificación{notifications.length !== 1 ? 'es' : ''}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Hook personalizado - IGUAL
export const useNotifications = () => {
  const [user] = useAuthState(auth);

  // Helper para obtener datos de perfil de usuario
  const fetchUserProfile = async (uid) => {
    if (!uid) return {};
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        return {
          customPhotoURL: data.customPhotoURL || null,
          photoURL: data.photoURL || null,
          nickname: data.nickname || null,
          displayName: data.displayName || null
        };
      }
    } catch (e) { /* ignorar */ }
    return {};
  };

  const sendFollowNotification = useCallback(async (followedUserId, followerName) => {
    if (!user || !followedUserId || followedUserId === user.uid) return;
    const { customPhotoURL, photoURL, nickname, displayName } = await fetchUserProfile(user.uid);
    await createNotification(followedUserId, 'follow', {
      followerName: nickname || displayName || followerName,
      followerId: user.uid,
      followerNickname: nickname || null,
      followerCustomPhotoURL: customPhotoURL,
      followerPhotoURL: photoURL
    });
  }, [user]);

  const sendRoutineCopiedNotification = useCallback(async (originalUserId, routineName, copierName) => {
    if (!user || !originalUserId || originalUserId === user.uid) return;
    const { customPhotoURL, photoURL, nickname, displayName } = await fetchUserProfile(user.uid);
    await createNotification(originalUserId, 'routine_copied', {
      routineName,
      userName: nickname || displayName || copierName,
      copierId: user.uid,
      copierNickname: nickname || null,
      copierCustomPhotoURL: customPhotoURL,
      copierPhotoURL: photoURL
    });
  }, [user]);

  const sendNewRoutineNotification = useCallback(async (authorId, authorName, routineData) => {
    if (!user || !authorId || authorId === user.uid) return;
    const { customPhotoURL, photoURL, nickname, displayName } = await fetchUserProfile(authorId);
    await createNotification(authorId, 'new_routine', {
      routineId: routineData.routineId,
      routineName: routineData.name,
      routineCategory: routineData.category,
      routineDifficulty: routineData.difficulty,
      authorId: authorId,
      authorName: nickname || displayName || authorName,
      authorNickname: nickname || null,
      authorCustomPhotoURL: customPhotoURL,
      authorPhotoURL: photoURL
    });
  }, [user]);

  const sendRoutineUpdatedNotification = useCallback(async (authorId, authorName, routineData) => {
    if (!user || !authorId || authorId === user.uid) return;
    const { customPhotoURL, photoURL, nickname, displayName } = await fetchUserProfile(authorId);
    await createNotification(authorId, 'routine_updated', {
      routineId: routineData.routineId,
      routineName: routineData.name,
      routineCategory: routineData.category,
      routineDifficulty: routineData.difficulty,
      authorId: authorId,
      authorName: nickname || displayName || authorName,
      authorNickname: nickname || null,
      authorCustomPhotoURL: customPhotoURL,
      authorPhotoURL: photoURL
    });
  }, [user]);

  const sendNewFollowerNotification = useCallback(async (followedUserId, followerName) => {
    if (!user || !followedUserId || followedUserId === user.uid) return;
    const { customPhotoURL, photoURL, nickname, displayName } = await fetchUserProfile(user.uid);
    await createNotification(followedUserId, 'new_follower', {
      followerName: nickname || displayName || followerName,
      followerId: user.uid,
      followerNickname: nickname || null,
      followerCustomPhotoURL: customPhotoURL,
      followerPhotoURL: photoURL
    });
  }, [user]);

  // NUEVO: Notificar a seguidores sobre dieta pública
  const sendNewDietNotification = useCallback(async (followerId, authorName, dietData) => {
    if (!user || !followerId || followerId === user.uid) return;
    // Obtener perfil del autor (el creador de la dieta)
    const { customPhotoURL, photoURL, nickname, displayName } = await fetchUserProfile(user.uid);
    await createNotification(followerId, 'new_diet', {
      dietId: dietData.dietId,
      dietName: dietData.name,
      dietCategory: dietData.category || null,
      dietDifficulty: dietData.difficulty || null,
      authorId: user.uid,
      authorName: nickname || displayName || authorName,
      authorNickname: nickname || null,
      authorCustomPhotoURL: customPhotoURL || null,
      authorPhotoURL: photoURL || null
    });
  }, [user]);

  // NUEVO: Notificar a seguidores sobre actualización de dieta pública
  const sendDietUpdatedNotification = useCallback(async (followerId, authorName, dietData) => {
    if (!user || !followerId || followerId === user.uid) return;
    const { customPhotoURL, photoURL, nickname, displayName } = await fetchUserProfile(user.uid);
    await createNotification(followerId, 'diet_updated', {
      dietId: dietData.dietId,
      dietName: dietData.name,
      dietCategory: dietData.category || null,
      dietDifficulty: dietData.difficulty || null,
      authorId: user.uid,
      authorName: nickname || displayName || authorName,
      authorNickname: nickname || null,
      authorCustomPhotoURL: customPhotoURL || null,
      authorPhotoURL: photoURL || null
    });
  }, [user]);

  return {
    sendFollowNotification,
    sendRoutineCopiedNotification,
    sendNewRoutineNotification,
    sendRoutineUpdatedNotification,
    sendNewFollowerNotification,
    // NUEVO
    sendNewDietNotification,
    sendDietUpdatedNotification
  };
};

// Componente interno para avatar robusto en notificaciones (visible en el desplegable del navbar)
const NotificationAvatar = ({ src, alt }) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  const effectiveSrc = (src && typeof src === 'string' && src.trim() && src !== '/default-avatar.png') ? src : null;

  React.useEffect(() => {
    setImgFailed(false);
  }, [effectiveSrc]);

  const handleError = React.useCallback(() => {
    setImgFailed(true);
  }, []);

  // Si no hay URL válida o la imagen falló, mostrar placeholder para que siempre se vea algo
  if (!effectiveSrc || imgFailed) {
    return (
      <span className="notification-avatar-fallback" role="img" aria-label={alt || 'Avatar'}>
        👤
      </span>
    );
  }

  return (
    <img
      src={effectiveSrc}
      alt={alt || 'Avatar'}
      className="notification-avatar"
      onError={handleError}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
    />
  );
};